import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

interface StorageCategory {
  name: string;
  size: number;
  fileCount: number;
  percentage: number;
  icon: string;
}

interface PurchasedStorageItem {
  amount: number;
  remaining: number;
  expiresAt: Date;
  daysUntilExpiry: number;
}

interface DetailedStorageUsage {
  categories: StorageCategory[];
  totalUsed: number;
  totalLimit: number;
  planType: string;
  planExpiresAt: Date | null;
  purchasedStorage: PurchasedStorageItem[];
}

export const useDetailedStorageUsage = () => {
  const { user } = useAuth();
  const [data, setData] = useState<DetailedStorageUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDetailedStorage = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        // Buscar dados de cada categoria sequencialmente
        const filesResult = await supabase.from('files').select('size').eq('user_id', user.id);
        const filesData = (filesResult.data || []) as Array<{ size: number }>;

        const legalFilesResult = await supabase.from('legal_chat_files').select('file_size').eq('user_id', user.id);
        const legalFilesData = (legalFilesResult.data || []) as Array<{ file_size: number }>;

        const casoKBResult = await supabase.from('caso_kb_links').select('file_size').eq('user_id', user.id);
        const casoKBData = (casoKBResult.data || []) as Array<{ file_size: number }>;

        const subscriptionResult = await supabase.from('subscriptions').select('plan_type, end_date, storage_limit').eq('user_id', user.id).eq('status', 'active').order('created_at', { ascending: false }).limit(1).maybeSingle();
        const subscriptionData = subscriptionResult.data as { plan_type: string; end_date: string; storage_limit: number } | null;

        const purchasedResult = await supabase.from('purchased_storage').select('storage_purchased, storage_remaining, expires_at').eq('user_id', user.id).eq('is_active', true);
        const purchasedStorageData = (purchasedResult.data || []) as Array<{ storage_purchased: number; storage_remaining: number; expires_at: string }>;

        // Calcular tamanhos e contagens
        const filesSize = filesData.reduce((acc, f) => acc + (f.size || 0), 0);
        const filesCount = filesData.length;

        const legalSize = legalFilesData.reduce((acc, f) => acc + (f.file_size || 0), 0);
        const legalCount = legalFilesData.length;

        const casoSize = casoKBData.reduce((acc, f) => acc + (f.file_size || 0), 0);
        const casoCount = casoKBData.length;

        const totalUsed = filesSize + legalSize + casoSize;

        // Buscar limite do plano diretamente do banco
        const planType = subscriptionData?.plan_type || 'free';
        const planLimit = subscriptionData?.storage_limit || (100 * 1024 * 1024); // Fallback 100MB
        const planExpiresAt = subscriptionData?.end_date ? new Date(subscriptionData.end_date) : null;

        // Storage comprado
        const purchasedStorage = purchasedStorageData.reduce((acc, ps) => acc + (ps.storage_purchased || 0), 0);
        const totalLimit = planLimit + purchasedStorage;

        // Calcular porcentagens em relação ao limite total
        const categories: StorageCategory[] = [
          {
            name: 'Documentos Gerais',
            size: filesSize,
            fileCount: filesCount,
            percentage: totalLimit > 0 ? (filesSize / totalLimit) * 100 : 0,
            icon: 'file-text'
          },
          {
            name: 'Chat Jurídico',
            size: legalSize,
            fileCount: legalCount,
            percentage: totalLimit > 0 ? (legalSize / totalLimit) * 100 : 0,
            icon: 'scale'
          },
          {
            name: 'Base de Casos',
            size: casoSize,
            fileCount: casoCount,
            percentage: totalLimit > 0 ? (casoSize / totalLimit) * 100 : 0,
            icon: 'book-open'
          }
        ];

        // Formatar storage comprado com validação
        const formattedPurchased = purchasedStorageData.map((ps) => {
          const expiresAt = new Date(ps.expires_at);
          const daysUntilExpiry = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
          return {
            amount: ps.storage_purchased || 0,
            remaining: ps.storage_remaining || 0,
            expiresAt,
            daysUntilExpiry
          };
        }).filter(ps => ps.amount > 0 && ps.remaining > 0);

        setData({
          categories,
          totalUsed,
          totalLimit,
          planType,
          planExpiresAt,
          purchasedStorage: formattedPurchased
        });
      } catch (error) {
        console.error('Erro ao buscar detalhes de armazenamento:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetailedStorage();
  }, [user]);

  return { data, isLoading };
};
